import React, { useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Check, X, Columns2, GitCompareArrows } from "lucide-react";

// Word-level diff (LCS) → segments of {type: same|add|del, text}
function diffWords(oldStr, newStr) {
  const a = oldStr.split(/(\s+)/);
  const b = newStr.split(/(\s+)/);
  const n = a.length, m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const segs = [];
  let i = 0, j = 0;
  const push = (type, text) => {
    const last = segs[segs.length - 1];
    if (last && last.type === type) last.text += text;
    else segs.push({ type, text });
  };
  while (i < n && j < m) {
    if (a[i] === b[j]) { push("same", a[i]); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { push("del", a[i]); i++; }
    else { push("add", b[j]); j++; }
  }
  while (i < n) { push("del", a[i]); i++; }
  while (j < m) { push("add", b[j]); j++; }
  return segs;
}

// Group consecutive non-"same" segments into a single reviewable change.
function buildChanges(segs) {
  const changes = [];
  let cur = null;
  segs.forEach((s, idx) => {
    if (s.type === "same") { if (cur) { changes.push(cur); cur = null; } }
    else {
      if (!cur) cur = { id: `c${changes.length}`, del: "", add: "", start: idx };
      if (s.type === "del") cur.del += s.text;
      else cur.add += s.text;
    }
  });
  if (cur) changes.push(cur);
  return changes;
}

const OLD_TEXT = "The petitioner is a resident within the jurisdiction of this Hon'ble Court. The respondents refused to relocate the transformer despite representations. The petitioner seeks a direction to relocate the transformer.";
const NEW_TEXT = "The petitioner is a permanent resident within the territorial jurisdiction of this Hon'ble Court. The respondents, being a State instrumentality, refused to relocate the transformer despite repeated representations and the statutory notice. The petitioner seeks an appropriate writ directing the respondents to relocate the transformer in accordance with the applicable safety regulations.";

export function DraftCompare({ open, onOpenChange, draft }) {
  const [mode, setMode] = useState("track"); // track | side
  const segs = useMemo(() => diffWords(OLD_TEXT, NEW_TEXT), []);
  const changes = useMemo(() => buildChanges(segs), [segs]);
  const [decisions, setDecisions] = useState({}); // id -> accept | reject

  const decide = (id, val) => setDecisions((d) => ({ ...d, [id]: val }));
  const acceptAll = () => setDecisions(Object.fromEntries(changes.map((c) => [c.id, "accept"])));
  const rejectAll = () => setDecisions(Object.fromEntries(changes.map((c) => [c.id, "reject"])));

  const changeAt = useMemo(() => {
    const map = {};
    changes.forEach((c) => { map[c.start] = c; });
    return map;
  }, [changes]);

  // Build merged text honouring decisions (pending adds kept, pending dels kept => shows proposed)
  const merged = useMemo(() => {
    let out = "";
    segs.forEach((s, idx) => {
      if (s.type === "same") { out += s.text; return; }
      const change = findChange(changes, idx);
      const dec = change ? decisions[change.id] : undefined;
      if (s.type === "add") { if (dec !== "reject") out += s.text; }
      else if (s.type === "del") { if (dec === "reject") out += s.text; }
    });
    return out.replace(/[ \t]+/g, " ").trim();
  }, [segs, changes, decisions]);

  const decided = Object.keys(decisions).length;

  const apply = () => {
    toast.success(`Applied ${decided} of ${changes.length} changes — draft updated to v${(draft?.version || 1) + 1}.`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="draft-compare-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Compare Versions — v{Math.max(1, (draft?.version || 2) - 1)} → v{draft?.version || 3}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            <button onClick={() => setMode("track")} className={cn("flex items-center gap-1 rounded-md px-3 py-1 text-xs font-medium", mode === "track" ? "bg-navy text-white" : "border border-line text-ink-2")}><GitCompareArrows className="h-3.5 w-3.5" /> Track Changes</button>
            <button onClick={() => setMode("side")} className={cn("flex items-center gap-1 rounded-md px-3 py-1 text-xs font-medium", mode === "side" ? "bg-navy text-white" : "border border-line text-ink-2")}><Columns2 className="h-3.5 w-3.5" /> Side by Side</button>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-ink-2">{decided}/{changes.length} reviewed</span>
            <Button size="sm" variant="outline" onClick={acceptAll}>Accept all</Button>
            <Button size="sm" variant="outline" onClick={rejectAll}>Reject all</Button>
          </div>
        </div>

        <div className="nyk-scroll max-h-[52vh] overflow-y-auto">
          {mode === "side" ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-line bg-surface p-4 text-sm leading-relaxed">
                <div className="mb-2 text-xs font-semibold uppercase text-ink-muted">Previous (v{Math.max(1, (draft?.version || 2) - 1)})</div>
                {segs.map((s, i) => s.type === "add" ? null : <span key={i} className={s.type === "del" ? "bg-danger-bg text-danger line-through" : "text-ink"}>{s.text}</span>)}
              </div>
              <div className="rounded-lg border border-line bg-card p-4 text-sm leading-relaxed">
                <div className="mb-2 text-xs font-semibold uppercase text-ink-muted">Current (v{draft?.version || 3})</div>
                {segs.map((s, i) => s.type === "del" ? null : <span key={i} className={s.type === "add" ? "bg-success-bg text-success underline decoration-success/50" : "text-ink"}>{s.text}</span>)}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border border-line bg-card p-4 text-sm leading-relaxed">
                {segs.map((s, i) => {
                  if (s.type === "same") return <span key={i} className="text-ink">{s.text}</span>;
                  const change = findChange(changes, i);
                  const dec = change ? decisions[change.id] : undefined;
                  if (s.type === "add") return <span key={i} className={cn("rounded px-0.5", dec === "reject" ? "hidden" : "bg-success-bg text-success underline decoration-success/50")}>{s.text}</span>;
                  return <span key={i} className={cn("rounded px-0.5", dec === "reject" ? "text-ink" : "bg-danger-bg text-danger line-through")}>{s.text}</span>;
                })}
              </div>

              <div className="space-y-2">
                <div className="text-xs font-semibold uppercase text-ink-muted">Changes ({changes.length})</div>
                {changes.map((c, i) => {
                  const dec = decisions[c.id];
                  return (
                    <div key={c.id} className={cn("rounded-lg border p-3", dec === "accept" ? "border-success/40 bg-success-bg/40" : dec === "reject" ? "border-danger/30 bg-danger-bg/30" : "border-line")} data-testid={`draft-change-${i}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1 text-sm">
                          {c.del && <div><span className="text-[10px] font-semibold uppercase text-danger">Removed</span> <span className="text-danger line-through">{c.del.trim()}</span></div>}
                          {c.add && <div><span className="text-[10px] font-semibold uppercase text-success">Added</span> <span className="text-success">{c.add.trim()}</span></div>}
                        </div>
                        <div className="flex shrink-0 gap-1">
                          <button onClick={() => decide(c.id, "accept")} title="Accept" className={cn("flex h-7 w-7 items-center justify-center rounded border", dec === "accept" ? "border-success bg-success text-white" : "border-line text-ink-2 hover:border-success")} data-testid={`accept-change-${i}`}><Check className="h-4 w-4" /></button>
                          <button onClick={() => decide(c.id, "reject")} title="Reject" className={cn("flex h-7 w-7 items-center justify-center rounded border", dec === "reject" ? "border-danger bg-danger text-white" : "border-line text-ink-2 hover:border-danger")} data-testid={`reject-change-${i}`}><X className="h-4 w-4" /></button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="rounded-lg border border-line bg-surface p-4 text-sm leading-relaxed">
                <div className="mb-2 text-xs font-semibold uppercase text-ink-muted">Merged Result</div>
                <p className="text-ink">{merged}</p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={apply} data-testid="apply-changes-btn">Apply {decided > 0 ? `(${decided})` : ""} Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function findChange(changes, idx) {
  // a change owns the contiguous run starting at its start; find by nearest start <= idx
  let found = null;
  changes.forEach((c) => { if (idx >= c.start && (!found || c.start > found.start)) found = c; });
  // ensure idx belongs to this run (no "same" between) — approximate: accept if within a small window
  return found;
}
